<template>
  <div class="container-fluid container-fullheight">
    <form
      :class="`d-flex flex-column justify-content-between h-100 ${Object(errors).length > 0 ? 'was-validated' : ''}`"
      @submit.prevent="handleSave"
      novalidate
    >
      <section class="pt-2">
        <h1 class="section-title py-0 m-0">
          New Location
        </h1>
        <hr>
      </section>
      <div class="row flex-grow-1">
        <div class="col-12 col-lg-6">
          <div class="scrollable-area">
            <div class="form-group">
              <label>Default Name*</label>
              <input
                type="text"
                v-model.trim="location.name"
                :class="`form-control ${errors['name'] ? 'is-invalid' : ''}`"
                :disabled="form.loading"
                required
              >
              <div class="invalid-feedback font-weight-bold">
                {{ errors['name'] }}
              </div>
            </div>
            <div
              class="form-row"
            >
              <div class="col-12 mb-2">
                <label class="m-0">Labels* <small class="text-muted">Spanish will be used by default.</small></label>
                <div :class="`invalid-feedback font-weight-bold ${!!errors['labels'] ? 'd-block' : ''}`">
                  {{ errors['labels'] }}
                </div>
              </div>
              <div
                v-for="language in languages"
                :class="`form-group col-6 col-md-3 ${errors['labels-'+language.code] ? 'is-invalid' : ''}`"
                :key="language.code"
              >
                <label class="mb-0 text-muted">{{ language.name }} ({{ language.code }}){{ language.code === 'es' ? '*' : '' }}</label>
                <input
                  type="text"
                  :class="`form-control ${errors['labels-'+language.code] ? 'is-invalid' : ''}`"
                  v-model.trim="location.labels[language.code]"
                  :disabled="form.loading"
                >
                <div :class="`invalid-feedback font-weight-bold ${!!errors['labels-'+language.code] ? 'd-block' : ''}`">
                  {{ errors['labels-'+language.code] }}
                </div>
              </div>
            </div>
            <hr>
            <div class="form-row">
              <div class="form-group col-12 col-sm-6 col-md">
                <label>Location Type*</label>
                <select
                  :class="`form-control ${errors['type'] ? 'is-invalid' : ''}`"
                  :disabled="form.loading"
                  v-model="location.type"
                  @change="onLocationTypeChange"
                >
                  <option :value="null">
                    Select...
                  </option>
                  <option
                    v-for="type in locationTypes"
                    :key="type.key"
                    :value="type.key"
                  >
                    {{ type.name }}
                  </option>
                </select>
                <div :class="`invalid-feedback font-weight-bold`">
                  {{ errors['type'] }}
                </div>
              </div>
              <div class="form-group col-12 col-sm-6 col-md">
                <label>Location Type Description*</label>
                <select
                  :class="`form-control ${errors['typeDescription'] ? 'is-invalid' : ''}`"
                  :disabled="form.loading"
                  v-model="location.typeDescription"
                >
                  <option :value="null">
                    Select...
                  </option>
                  <option
                    v-for="type in locationTypeDescriptions"
                    :key="type.key"
                    :value="type.key"
                  >
                    {{ type.name }}
                  </option>
                </select>
                <div :class="`invalid-feedback font-weight-bold ${!!errors['typeDescription'] ? 'd-block' : ''}`">
                  {{ errors['typeDescription'] }}
                </div>
              </div>
              <div class="form-group col-12 col-sm-6 col-md">
                <label>XML Location Type*</label>
                <select
                  :class="`form-control ${errors['xmlType'] ? 'is-invalid' : ''}`"
                  :disabled="form.loading"
                  v-model="location.xmlType"
                >
                  <option :value="null">
                    Select...
                  </option>
                  <option
                    v-for="type in xmlLocationTypes"
                    :key="type.key"
                    :value="type.key"
                  >
                    {{ type.name }}
                  </option>
                </select>
                <div :class="`invalid-feedback font-weight-bold ${!!errors['xmlType'] ? 'd-block' : ''}`">
                  {{ errors['xmlType'] }}
                </div>
              </div>
            </div>
            <hr>
            <div class="form-row">
              <div class="form-group col-12 col-sm-6 col-md">
                <label class="m-0">Significant Center*</label>
                <div :class="`invalid-feedback font-weight-bold ${!!errors['significantCenter'] ? 'd-block' : ''}`">
                  {{ errors['significantCenter'] }}
                </div>
                <div class="form-row mt-2">
                  <div class="input-group col-12 col-md-6 mb-2 mb-md-0">
                    <div class="input-group-prepend">
                      <div class="input-group-text">
                        Latitude*
                      </div>
                    </div>
                    <input
                      type="text"
                      :class="`form-control ${errors['latitude'] ? 'is-invalid' : ''}`"
                      v-model.number="location.latitude"
                      :disabled="form.loading"
                      readonly
                    >
                    <div class="invalid-feedback font-weight-bold">
                      {{ errors['latitude'] }}
                    </div>
                  </div>
                  <div class="input-group col-12 col-md-6">
                    <div class="input-group-prepend">
                      <div class="input-group-text">
                        Longitude*
                      </div>
                    </div>
                    <input
                      type="text"
                      v-model.number="location.longitude"
                      :class="`form-control ${errors['longitude'] ? 'is-invalid' : ''}`"
                      :disabled="form.loading"
                      readonly
                    >
                    <div class="invalid-feedback font-weight-bold">
                      {{ errors['longitude'] }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <hr>
            <div class="form-row">
              <div class="form-group col">
                <label>Slugs* <small class="text-muted">Slugs should contains letters and hyphens only, e.g. 'this-is-a-good-slug'</small></label>
                <div
                  v-for="language in [{code:'default',name:'Default'}].concat(languages)"
                  :key="language.code"
                  :class="`${language.code === 'default' ? 'mb-3' : 'mb-1'}`"
                >
                  <div class="input-group">
                    <div
                      class="input-group-prepend"
                    >
                      <div
                        class="input-group-text w-100 font-weight-bold text-right"
                        style="min-width:135px;"
                      >
                        {{ language.name }} {{ language.code === 'default' ? '' : `(${language.code})` }}<template v-if="language.code === 'default'">
                          *
                        </template>
                      </div>
                    </div>
                    <input
                      type="text"
                      :class="`form-control ${errors[`slug-${language.code}`] ? 'is-invalid' : ''}`"
                      v-model="slugUI.statuses[language.code].value"
                      @change="validateSlug(language.code)"
                      :disabled="form.loading"
                    >
                    <div class="input-group-append">
                      <div class="input-group-text">
                        <span
                          v-if="slugUI.statuses[language.code].status === 'ok'"
                          class="text-success"
                        >
                          Ok
                        </span>
                        <span
                          v-if="slugUI.statuses[language.code].status === 'duplicate'"
                          class="text-danger"
                        >
                          Duplicate
                        </span>
                        <span
                          v-if="slugUI.statuses[language.code].status === 'invalid'"
                          class="text-danger"
                        >
                          Invalid
                        </span>
                      </div>
                    </div>
                    <div
                      class="invalid-feedback font-weight-bold"
                      v-if="errors[`slug-${language.code}`]"
                    >
                      {{ errors[`slug-${language.code}`] }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="scrollable-area">
            <div class="form-group">
              <label class="m-0">Location Shape* <small class="text-muted">Providing a Shape will automatically set the ShapeBounds &amp; ShapeCenter.</small></label>
              <div :class="`invalid-feedback font-weight-bold ${!!errors['feature'] ? 'd-block' : ''}`">
                {{ errors['feature'] }}
              </div>
              <LocationMap
                class="mt-2 mb-2 "
                :allowed-types="['MultiPolygon','Polygon']"
                :disabled="form.loading"
                @update="onGeometryUpdate"
              />
            </div>
            <div class="form-group">
              <label class="m-0">Location Relations* <small class="text-muted">Provide a Shape to automatically construct the hierarchy.</small></label>
              <div :class="`invalid-feedback font-weight-bold ${!!errors['relations'] ? 'd-block' : ''}`">
                {{ errors['relations'] }}
              </div>
              <template
                v-for="locationType in allLocationTypes"
              >
                <div
                  :class="`invalid-feedback font-weight-bold ${!!errors['relations-'+locationType.key] ? 'd-block' : ''}`"
                  :key="locationType.key"
                  v-if="errors['relations-'+locationType.key]"
                >
                  {{ errors['relations-'+locationType.key] }}
                </div>
              </template>
              <div class="mt-2">
                <button
                  class="d-inline-block btn btn-sm btn-outline-primary"
                  type="button"
                  @click="getHierarchy"
                  :disabled="!location.latitude || !location.longitude"
                >
                  Build from Significant Center
                </button>
                <table class="table mt-2 mb-1">
                  <thead>
                    <tr>
                      <th scope="col">
                        Predicate
                      </th>
                      <th scope="col">
                        Object
                      </th>
                      <th scope="col">
                        Type
                      </th>
                      <th scope="col">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="relation in locationRelations"
                      :key="relation.object"
                      :class="relation.new ? 'table-success' : ''"
                    >
                      <td>{{ locationPredicates.find( p => p.key === relation.predicate ).name }}</td>
                      <td>{{ relatedLocations.find(x => x.id === relation.object ).labels.es }} <small>({{ relation.object }})</small></td>
                      <td>{{ allLocationTypes.find( t => t.key === relatedLocations.find(x => x.id === relation.object ).type ).name }}</td>
                      <td>
                        <button
                          type="button"
                          class="btn btn-link p-0 d-block mx-auto text-danger"
                          @click="deleteRelation(relation)"
                        >
                          Delete
                        </button>
                        <a
                          :href="`/locations/${relation.object}`"
                          target="_blank"
                          class="btn btn-link p-0 d-block mx-auto"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div class="form-row">
                  <div class="form-group col-md-3">
                    <select
                      class="form-control"
                      v-model="relationsUI.newRelationPredicate"
                      :disabled="form.loading"
                    >
                      <option value="locatedIn">
                        Located In
                      </option>
                    </select>
                  </div>
                  <div class="form-group col-md-7">
                    <vue-typeahead-bootstrap
                      placeholder="Start typing a location name..."
                      v-model="relationsUI.query"
                      @hit="relationsUI.newRelation = $event; relationsUI.errors.location = null"
                      :min-matching-chars="2"
                      :data="relationsUI.suggestions"
                      :serializer="displayName"
                      :disable-sort="true"
                      :show-all-results="true"
                      :disabled="form.loading"
                    />
                    <div
                      class="invalid-feedback font-weight-bold"
                      v-if="relationsUI.errors.location"
                    >
                      {{ relationsUI.errors.location }}
                    </div>
                  </div>
                  <div class="form-group col-md-2">
                    <button
                      class="btn btn-outline-primary w-100"
                      type="button"
                      @click="addRelation"
                      :disabled="form.loading"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <button
            type="submit"
            class="btn w-100 font-weight-bold"
            :class="{
              'btn-danger': Object.keys(errors).length > 0,
              'btn-primary': Object.keys(errors).length === 0 && !form.loading,
              'btn-warning': Object.keys(errors).length === 0 && form.loading
            }"
            :disabled="form.loading || Object.keys(errors).length > 0"
          >
            <template v-if="Object.keys(errors).length > 0">
              <p class="m-0 w-100 text-center font-weight-bold">
                Cannot Save with pending errors ({{ Object.keys(errors).length }})
              </p>
            </template>
            <template v-else>
              <span v-if="!form.loading">Save Location</span>
              <span v-else>Saving...</span>
              <div
                v-if="form.loading"
                class="spinner-border spinner-border-sm text-primary"
                role="status"
              >
                <span class="sr-only">Saving...</span>
              </div>
            </template>
          </button>
        </div>
      </div>
    </form>
  </div>
</template>
<script>
import http from '../../http';
import languages from '../../models/languages';
import locationTypes, { xmlLocationTypes, locationPredicates, locationTypeDescriptions, requiredLocationParentTypes, getLocationTypeSortValue } from '../../models/location-types';
import notify from '../../notifications';
import { newLocation } from '../../models/location';
import LocationMap from './LocationMap.vue';

const initialSlugStatuses = {
    'default': {
        status: 'ok',
        value: ''
    }
};

languages.forEach( l => {
    initialSlugStatuses[ l.code ] = {
        status: 'ok',
        value: ''
    };
} );

const defaultLocation = newLocation();

export default {
    data() {
        return {
            location: defaultLocation,
            relatedLocations: [],
            loadingRelatedLocations: false,
            form: {
                loading: false
            },
            slugUI: {
                statuses: initialSlugStatuses
            },
            relationsUI: {
                newRelationPredicate: 'locatedIn',
                newRelation: null,
                suggestions: [],
                query: '',
                errors: {}
            },
            errors: {}
        };
    },
    computed: {
        languages() {
            return languages;
        },
        locationTypes() {
            return locationTypes.filter( t => [ 'entity', 'district', 'neighbourhood' ].includes( t.key ) );
        },
        xmlLocationTypes() {
            return xmlLocationTypes.filter( t => t.for.includes( this.location.type ) || t.for.includes( '*' ) );
        },
        locationTypeDescriptions() {
            return locationTypeDescriptions.filter( t => t.for.includes( this.location.type ) || t.for.includes( '*' ) );
        },
        locationPredicates() {
            return locationPredicates;
        },
        allLocationTypes() {
            return locationTypes;
        },
        locationRelations() {
            if ( !this.location || !this.location.relations || !this.relatedLocations ) {
                return [];
            }

            const relations = this.location.relations || [];

            return relations.sort( ( a, b ) => {
                const aValue = getLocationTypeSortValue( this.relatedLocations.find( x => x.id === a.object ).type );
                const bValue = getLocationTypeSortValue( this.relatedLocations.find( x => x.id === b.object ).type );
                return aValue - bValue;
            } );
        }
    },
    methods: {
        onGeometryUpdate( event ) {
            this.location.latitude = null;
            this.location.longitude = null;
            this.location.bbox = null;
            this.location.feature = null;

            if ( event.center ) {
                this.location.latitude = event.center.latitude;
                this.location.longitude = event.center.longitude;
            }

            if ( event.bbox ) {
                this.location.bbox = event.bbox;
            }

            if ( event.wkt && event.wkt.length > 0 ) {
                this.location.feature = event.wkt;
            }

            this.validate();
        },
        async validateSlug( language ) {
            this.form.loading = true;

            if ( !this.slugUI.statuses[ language ] ) {
                return;
            }

            if ( this.errors[ `slug-${language}` ] ) {
                this.$delete( this.errors, `slug-${language}` );
            }

            const slug =  this.slugUI.statuses[ language ].value;

            if ( slug === '' ) {
                this.slugUI.statuses[ language ].status = 'ok';
                this.form.loading = false;
                return;
            }

            const params = new URLSearchParams();
            params.set( 'slug', slug );

            if ( language !== 'default' ) {
                params.set( 'language', language );
            }

            let response;

            try {
                response = await http.get( `slugs/validate?${params.toString()}` );
            } catch ( err ) {
                notify( {
                    type: 'error',
                    title: 'Slug validation request failed',
                    text: err.toString()
                } );
                this.form.loading = false;
                return;
            }

            if ( response.status !== 200 ) {
                notify( {
                    type: 'error',
                    title: `Slug validation request failed with status ${response.status}`
                } );
                this.form.loading = false;
                return;
            }

            const body = await response.json();

            const isDuplicateOfSelf = body.status === 'duplicate' && body.matches.length === 1 && body.matches[ 0 ].key === `location:${this.location.id}`;

            if ( isDuplicateOfSelf ) {
                this.slugUI.statuses[ language ].status = 'ok';
                this.form.loading = false;
                return;
            }

            if ( !body.passed ) {
                notify( {
                    type: 'error',
                    title: 'Slug Validation Failed',
                    text: 'See status for details'
                } );
            }

            this.slugUI.statuses[ language ].status = body.status;

            switch ( body.status ) {
            case 'ok':
                this.location.slugs[ language ] = slug;
                break;
            case 'invalid':
                this.errors[ `slug-${language}` ] = 'The slug format is invalid';
                break;
            case 'duplicate':
                this.errors[ `slug-${language}` ] = `There is an existing slug that matches the one provided (${body.matches.map( m => m.key ).join( ',' )})`;
                this.slugUI.statuses[ language ].matches = body.matches;
                break;
            }

            this.validate();

            this.form.loading = false;
        },
        validate() {
            const location = this.location;
            const errs = {};

            if ( location.name.length === 0 ) {
                errs[ 'name' ] = 'A Default Name is required';
            }

            if ( location.latitude > 90 || location.latitude < -90 ) {
                errs[ 'latitude' ] = 'Latitudes should be between -90 & 90';
            }

            if ( location.longitude > 180 || location.longitude < -180 ) {
                errs[ 'longitude' ] = 'Longitudes should be between -180 & 180';
            }

            if ( !location.relations || location.relations.length === 0 ) {
                errs[ 'relations' ] = 'A location hierarchy is required. Build it manually or from the Significant Center.';
            } else if ( location.type ) {
                const requiredParentTypes = requiredLocationParentTypes[ location.type ];

                requiredParentTypes.forEach( requiredType => {
                    if ( !location.relations.find( p => p.objectType === requiredType ) ) {
                        errs[ 'relations-' + requiredType ] = `A parent ${locationTypes.find( t => t.key === requiredType ).name} is required`;
                    }
                } );
            }

            if ( !location.feature ) {
                errs[ 'feature' ] = 'A shape is required';
            }

            if ( !location.latitude ) {
                errs[ 'latitude' ] = 'A latitude is required';
            }

            if ( !location.longitude ) {
                errs[ 'longitude' ] = 'A longitude is required';
            }

            if ( !location.type ) {
                errs[ 'type' ] = 'A Location Type is required';
            }

            if ( !location.typeDescription ) {
                errs[ 'typeDescription' ] = 'A Location Type Description is required';
            }

            if ( !location.xmlType ) {
                errs[ 'xmlType' ] = 'A XML Type is required';
            }

            if ( !location.labels[ 'es' ] ) {
                errs[ 'labels-es' ] = 'A Spanish label is required';
            }

            if ( Object.keys( location.slugs ).length === 0 || Object.values( location.slugs ).every( s => !s ) ) {
                errs[ 'slug-default' ] = 'Provide a default slug if not providing language specific slugs';
            }

            Object.keys( this.slugUI.statuses ).forEach( language => {
                switch ( this.slugUI.statuses[ language ].status ) {
                case 'invalid':
                    errs[ `slug-${language}` ] = 'The slug format is invalid';
                    break;
                case 'duplicate':
                    errs[ `slug-${language}` ] = `There is an existing slug that matches the one provided (${ this.slugUI.statuses[ language ].matches.map( m => m.key ).join( ',' )})`;
                    break;
                }
            } );

            this.errors = errs;

            return Object.keys( this.errors ).length === 0;
        },
        async handleSave() {
            this.form.loading = true;

            const valid = this.validate();

            if ( !valid ) {
                this.form.loading = false;
                return;
            }

            const payload = JSON.parse( JSON.stringify( this.location ) );

            Object.keys( payload.labels ).forEach( lang => {
                if ( !payload.labels[ lang ] ) {
                    delete payload.labels[ lang ];
                }
            } );

            Object.keys( this.slugUI.statuses ).forEach( lang => {
                if ( this.slugUI.statuses[ lang ].value.length > 0 ) {
                    payload.slugs[ lang ] = this.slugUI.statuses[ lang ].value;
                } else {
                    delete payload.slugs[ lang ];
                }
            } );

            payload.relations = payload.relations.map( p => {
                return {
                    object: p.object,
                    subject: 0,
                    predicate: p.predicate
                };
            } );

            const response = await http.post( 'locations', payload );

            if ( response.status === 500 ) {
                notify( {
                    type: 'error',
                    title: 'Failed to Create Location'
                } );
                this.form.loading = false;
                return;
            }

            const body = await response.json();

            if ( response.status === 400 ) {
                let text = 'See errors on the form';

                if ( body.errorMsg ) {
                    text = `${body.errorMsg} (${text})`;
                }

                notify( {
                    type: 'error',
                    title: 'Failed to Create Location',
                    text: text
                } );

                if ( body.fieldErrors ) {
                    Object.keys( body.fieldErrors ).forEach( k => {
                        this.errors[ k ] = body.fieldErrors[ k ];
                    } );
                }

                this.form.loading = false;

                return;
            }

            const navigationPath = `/locations/${body.locationID}`;

            notify( {
                type: 'success',
                title: 'Location Created',
                text: `Navigating to ${navigationPath}`
            } );

            this.form.loading = false;

            this.reset();

            this.$router.push( {
                path: navigationPath
            } );
        },
        async getHierarchy() {
            this.form.loading = true;

            const payload = {
                latitude: this.location.latitude,
                longitude: this.location.longitude
            };

            const response = await http.post( 'locations/get-hierarchy', payload );

            if ( response.status !== 200 ) {
                notify( {
                    type: 'error',
                    title: 'Failed to get Hierarchy'
                } );
                this.form.loading = false;
                return;
            }

            let hierarchy = await response.json() || '[]';

            hierarchy = hierarchy.filter( relation => relation.id !== this.location.id );

            if ( this.location.relations ) {
                hierarchy = hierarchy.filter( relation => !this.location.relations.find( r => r.predicate === 'locatedIn' && r.objectType === relation.type && r.object === relation.id ) );
            }

            const relations = [
                ...this.location.relations || [],
                ...hierarchy.map( x => {
                    return {
                        new: true,
                        predicate: 'locatedIn',
                        objectName: x.name,
                        objectType: x.type,
                        object: x.id
                    };
                } )
            ];

            await this.getRelatedLocations( relations );

            this.location.relations = relations;

            this.validate();

            this.form.loading = false;
        },
        deleteRelation( relation ) {
            this.location.relations = this.location.relations.filter( r => {
                if ( r.predicate === relation.predicate && r.object === relation.object ) {
                    return false;
                }
                return true;
            } );
        },
        async addRelation() {
            this.relationsUI.errors = {};

            if ( this.relationsUI.newRelation === null ) {
                this.relationsUI.errors.location = 'Select a location';
                return;
            }

            const disallowedDuplicateTypes = [
                'country',
                'region',
                'island',
                'coast'
            ];

            const existing = this.location.relations.find( relation => {
                const location = this.relatedLocations.find( l => l.id === relation.object );

                return (
                    location.type === this.relationsUI.newRelation.type &&
                    disallowedDuplicateTypes.includes( location.type )
                );
            } );

            if ( existing ) {
                this.relationsUI.errors.location = `Duplicate parent location of type ${locationTypes.find( t => t.key === this.relationsUI.newRelation.type ).name} found`;
                this.relationsUI.newRelation = null;
                this.relationsUI.query = '';
                return;
            }

            const relations = [
                ...this.location.relations,
                {
                    subject: this.location.id,
                    object: this.relationsUI.newRelation.id,
                    predicate: this.relationsUI.newRelationPredicate,
                    new: true
                }
            ];

            await this.getRelatedLocations( relations );

            this.location.relations = relations;

            this.relationsUI.newRelation = null;
            this.relationsUI.query = '';
            this.relationsUI.suggestions = [];
        },
        parentName( location ) {
            let parentMunicipalities = location.relations.filter( r => r.objectType == 'municipality' );
            if ( parentMunicipalities.length > 0 ) {
                // case with multiple municipalities
                return `in ${parentMunicipalities.map( m => m.objectName ).join( '/' )} municipality`;
            }

            let parentProvinces = location.relations.filter( r => r.objectType == 'province' );
            if ( parentProvinces.length > 0 ) {
                return `in ${parentProvinces[ 0 ].objectName} province`;
            }

            return '';
        },
        displayName( location ) {
            let parentName = this.parentName( location );
            return `${location.name} (${location.id} ${location.type}) ${parentName}`;
        },
        async loadSuggestions( q ) {
            if ( !q || q.length < 2 ) {
                this.relationsUI.suggestions = [];
                this.relationsUI.newRelation = null;
                return;
            }

            const searchParams = new URLSearchParams();
            searchParams.set( 'limit', '20' );
            searchParams.set( isNaN( q ) ? 'freetext' : 'id', q );

            const response = await http.get( `locations?${searchParams.toString()}` );

            if ( response.status !== 200 ) {
                notify( {
                    type: 'error',
                    text: 'Something went wrong',
                    title: 'Failed to load locations'
                } );
            } else {
                this.relationsUI.suggestions = await response.json();
            }
        },
        reset() {
            this.location = newLocation();
            this.relatedLocations = [];
            this.slugUI.statuses = initialSlugStatuses;
            this.relationsUI.newRelation = null;
            this.relationsUI.suggestions = [];
            this.relationsUI.query = '';
            this.relationsUI.errors = {};
            this.errors = {};
            this.form.loading = false;
        },
        onLocationTypeChange() {
            this.location.typeDescription = this.locationTypeDescriptions[ 0 ].key;
            this.location.xmlType = this.xmlLocationTypes[ 0 ].key;
        },
        async getRelatedLocations( relations ) {
            this.loadingRelatedLocations = true;
            const locationRequests = [];

            relations.forEach( relation => {
                locationRequests.push( new Promise( async ( resolve ) => {
                    const response = await http.get( `locations/${relation.object}?includeShape=false` );
                    resolve( {
                        relation,
                        response
                    } );
                } ) );
            } );

            const results = await Promise.all( locationRequests );

            if ( results.every( r => r.response.status === 200 ) ) {
                for ( let i = 0; i < results.length; i++ ) {
                    this.relatedLocations = [
                        ...this.relatedLocations,
                        await results[ i ].response.json()
                    ];
                }
            } else {
                notify( {
                    type: 'error',
                    title: 'Failed loading related locations',
                    text: 'See status for details'
                } );
            }

            this.loadingRelatedLocations = false;
        }
    },
    beforeRouteEnter( to, from, next ) {
        next( vm => {
            vm.reset();
        } );
    },
    watch: {
        'relationsUI.query'( q ) {
            this.loadSuggestions( q );
        },
        'location': {
            deep: true,
            handler() {
                this.validate();
            }
        }
    },
    components: {
        LocationMap
    }
};
</script>

<style>
.scrollable-area {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100vh - 215px);
  scrollbar-width: thin;
  padding: 0 6px 0 0;
}
</style>



// WEBPACK FOOTER //
// src/components/locations/LocationCreate.vue